import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import type {
    NavigationPageVariablesSideSheet,
    NavigationScreenSpaceState,
} from '@gitbook/app-navigation';
import type { HiveWorkingBranch } from '@gitbook/hive-client';
import type { Revision, RevisionPageDocument } from '@gitbook/hive-core';
import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import { SidePanelSheet } from '@gitbook/react-uikit';
import { LoadingPanel } from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useCurrentHivePage, VariablesForm } from '../../../Components';
import { useSetAnalyticsScreen, useSideSheetNavigation } from '../../../navigation';

const tabs = [
    {
        key: 'page',
        label: 'This page',
    },
    {
        key: 'space',
        label: 'All pages',
    },
];

/**
 * Side panel to view and edit variables
 */
export function VariablesSidePanel(props: {
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    screen: NavigationScreenSpaceState;
    readOnly: boolean;
}): React.ReactElement {
    const { analyticsContext, screen, readOnly } = props;

    const { close, setSelectedTab, selectedTab } =
        useSideSheetNavigation<NavigationPageVariablesSideSheet>('variables', 'space');

    const hiveBranch = useHiveBranch();

    // Get revision and page
    const { revision } = useHiveRevision();
    const [page] = useCurrentHivePage(revision, screen.page);

    useSetAnalyticsScreen(
        !page
            ? null
            : {
                  ...analyticsContext,
                  screen: 'space_page_variables',
                  page: page.id,
              },
        [analyticsContext]
    );

    return (
        <SidePanelSheet
            title="Variables"
            onClose={close}
            tabs={tabs}
            selectedTab={selectedTab}
            onSelectedTabChange={setSelectedTab}
        >
            <React.Suspense fallback={<LoadingPanel />}>
                {page && selectedTab === 'page' ? (
                    <PageVariablesForm
                        hiveBranch={hiveBranch}
                        page={page}
                        readOnly={readOnly}
                        analyticsContext={analyticsContext}
                    />
                ) : null}
                {selectedTab === 'space' ? (
                    <SpaceVariablesForm
                        hiveBranch={hiveBranch}
                        revision={revision}
                        readOnly={readOnly}
                        analyticsContext={analyticsContext}
                    />
                ) : null}
            </React.Suspense>
        </SidePanelSheet>
    );
}

function PageVariablesForm(props: {
    hiveBranch: HiveWorkingBranch | null;
    page: RevisionPageDocument;
    readOnly: boolean;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { hiveBranch, page, readOnly, analyticsContext } = props;

    return (
        <VariablesForm
            description="Define variables to store values that you can reuse in this page."
            className="flex-1"
            prefix="page.vars."
            values={page.variables ?? {}}
            onChange={(values) => {
                if (readOnly) {
                    return;
                }

                assertIsDefined(hiveBranch);
                const previous = page.variables ?? {};

                for (const name of Object.keys(values)) {
                    if (!(name in previous)) {
                        analytics.logEvent({
                            ...analyticsContext,
                            page: page.id,
                            type: 'page_add_variable',
                            variableName: name,
                        });
                    } else if (previous[name] !== values[name]) {
                        analytics.logEvent({
                            ...analyticsContext,
                            page: page.id,
                            type: 'page_edit_variable',
                            variableName: name,
                        });
                    }
                }

                for (const name of Object.keys(previous)) {
                    if (!(name in values)) {
                        analytics.logEvent({
                            ...analyticsContext,
                            page: page.id,
                            type: 'page_remove_variable',
                            variableName: name,
                        });
                    }
                }

                hiveBranch.change((change) => {
                    change.updatePageVariables(page.id, values, { replace: true });
                });
            }}
            readOnly={readOnly}
        />
    );
}

function SpaceVariablesForm(props: {
    hiveBranch: HiveWorkingBranch | null;
    revision: Revision;
    readOnly: boolean;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { hiveBranch, revision, readOnly, analyticsContext } = props;

    return (
        <VariablesForm
            description="Define variables to store values that you can reuse in all pages of this space."
            prefix="space.vars."
            values={revision.variables ?? {}}
            onChange={(values) => {
                if (readOnly) {
                    return;
                }

                assertIsDefined(hiveBranch);
                const previous = revision.variables ?? {};

                for (const name of Object.keys(values)) {
                    if (!(name in previous)) {
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'space_add_variable',
                            variableName: name,
                        });
                    } else if (previous[name] !== values[name]) {
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'space_edit_variable',
                            variableName: name,
                        });
                    }
                }

                for (const name of Object.keys(previous)) {
                    if (!(name in values)) {
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'space_remove_variable',
                            variableName: name,
                        });
                    }
                }

                hiveBranch.change((change) => {
                    change.updateRevisionVariables(values, { replace: true });
                });
            }}
            readOnly={readOnly}
            className="flex-1"
        />
    );
}
